import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Send, FileText, Check, Loader2, AlertCircle, FolderCheck, Image as ImageIcon,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";

const isImage = (ct) => (ct || "").toLowerCase().startsWith("image/");

export const LenderPackModal = ({ form, onClose, onBuilt }) => {
  const [lenderName, setLenderName] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [compressImages, setCompressImages] = useState(true);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [customerName, setCustomerName] = useState("");
  const [productLabel, setProductLabel] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/forms/${form.id}/lender-pack/selection`,
          authHeaders(),
        );
        if (!alive) return;
        setDocs(data.documents || []);
        setCustomerName(data.customer_name || "");
        setProductLabel(data.application_label || "");
        // Pre-tick: the pre-ticked docs from the server plus the Application
        // PDF (if filed) — the user asked for this to be included by default.
        const pre = new Set(
          (data.documents || [])
            .filter((d) =>
              d.default_ticked ||
              (d.category || "").toLowerCase().endsWith("application"),
            )
            .map((d) => d.id),
        );
        setSelected(pre);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Could not load the customer's documents",
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [form.id]);

  const grouped = useMemo(() => {
    const by = {};
    for (const d of docs) {
      const k = d.category || "Other";
      (by[k] = by[k] || []).push(d);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const selectAll = () => setSelected(new Set(docs.map((d) => d.id)));
  const clearAll = () => setSelected(new Set());

  const build = async () => {
    if (!lenderName.trim()) {
      toast.error("Lender name is required");
      return;
    }
    if (selected.size === 0) {
      toast.error("Pick at least one document to include");
      return;
    }
    setWorking(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/forms/${form.id}/lender-pack`,
        {
          lender_name: lenderName.trim(),
          cover_note: coverNote,
          document_ids: Array.from(selected),
          compress_images: compressImages,
        },
        authHeaders(),
      );
      toast.success(`Pack ready · ${data.filename}`);
      // Trigger a download in the browser via the signed-url endpoint.
      const customerId = form.customer_id;
      const dl = `${API}/admin/customers/${customerId}/documents/${data.document_id}/file?download=true`;
      // Use a hidden anchor + the admin token on an authenticated fetch so
      // the browser both saves the file AND opens it as a download.
      try {
        const r = await axios.get(dl, { ...authHeaders(), responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (dlErr) {
        // Even if the download fails, the pack is saved — tell the user
        // where to find it.
        toast.info("Pack saved under Documents · open from there if the download didn't start.");
      }
      onBuilt?.();
      onClose();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not build the lender pack",
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="lender-pack-modal"
      data-lenis-prevent
    >
      <div className="bg-cream w-full max-w-2xl rounded-lg shadow-2xl mt-8 mb-10">
        <div className="flex items-center justify-between px-6 py-5 border-b border-forest/10">
          <div>
            <h3 className="font-serif text-2xl text-forest">Build lender pack</h3>
            {customerName && (
              <p className="text-[12px] text-sage mt-0.5">
                <span className="font-semibold text-forest">{customerName}</span> ·{" "}
                {productLabel || "Application"}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="lender-pack-close"
            className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded"
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <label className="block">
            <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
              Lender name <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              value={lenderName}
              onChange={(e) => setLenderName(e.target.value)}
              data-testid="lender-pack-name"
              placeholder="e.g. Halifax, HSBC, Together Money"
              className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
              Covering note <span className="text-forest/40 text-[10.5px]">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              data-testid="lender-pack-note"
              placeholder="A short note that will appear on the cover page."
              className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y"
            />
          </label>

          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">
                Documents to include
              </span>
              <div className="flex gap-3 items-center">
                <button
                  type="button"
                  onClick={selectAll}
                  data-testid="lender-pack-select-all"
                  className="text-[11.5px] font-semibold text-forest hover:text-gold-dark"
                >
                  Select all
                </button>
                <span className="text-forest/20">·</span>
                <button
                  type="button"
                  onClick={clearAll}
                  data-testid="lender-pack-clear-all"
                  className="text-[11.5px] font-semibold text-forest hover:text-gold-dark"
                >
                  Clear
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-sage mt-3">Loading documents…</p>
            ) : docs.length === 0 ? (
              <div
                className="mt-3 bg-amber-50 border border-amber-200 rounded px-4 py-3 flex gap-3"
                data-testid="lender-pack-no-docs"
              >
                <AlertCircle size={16} strokeWidth={1.8} className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-amber-800">
                  No filed documents yet for this customer. Click <strong>File to
                  customer documents</strong> first (or upload documents on the Documents
                  tab) and then build the pack.
                </p>
              </div>
            ) : (
              <div className="mt-2 max-h-72 overflow-y-auto border border-forest/10 rounded divide-y divide-forest/10 bg-white" data-lenis-prevent>
                {grouped.map(([cat, items]) => (
                  <div key={cat}>
                    <div className="px-3 py-1.5 bg-cream-3/60 text-[10.5px] tracking-[0.14em] uppercase font-bold text-forest/60">
                      {cat}
                    </div>
                    {items.map((d) => {
                      const Icon = isImage(d.content_type) ? ImageIcon : FileText;
                      const checked = selected.has(d.id);
                      return (
                        <label
                          key={d.id}
                          data-testid={`lender-pack-doc-${d.id}`}
                          className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-cream-3/40 ${
                            checked ? "bg-gold-soft/10" : ""
                          }`}
                        >
                          <span
                            className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                              checked
                                ? "bg-forest border-forest text-cream"
                                : "border-forest/30 bg-white"
                            }`}
                          >
                            {checked && <Check size={11} strokeWidth={3} />}
                          </span>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(d.id)}
                            className="hidden"
                          />
                          <Icon size={13} strokeWidth={1.8} className="text-forest/50 shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[12.5px] font-semibold text-forest truncate">
                              {d.filename || d.category}
                            </p>
                            {d.note && (
                              <p className="text-[11px] text-sage truncate">{d.note}</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <label className="mt-3 flex items-center gap-2 text-[12px] text-forest/70">
              <input
                type="checkbox"
                checked={compressImages}
                onChange={(e) => setCompressImages(e.target.checked)}
                data-testid="lender-pack-compress"
              />
              Compress scanned images for a smaller lender-ready file
            </label>
          </div>

          <div className="bg-cream-3 border border-forest/10 rounded px-4 py-3 flex items-start gap-3">
            <FolderCheck size={16} strokeWidth={1.8} className="text-gold-dark shrink-0 mt-0.5" />
            <p className="text-[12px] text-forest/80">
              The pack is saved under <strong>Lender Submission Pack</strong> on the
              customer's Documents and downloads to your device in one click.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-forest/10 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11.5px] text-sage">
            <span className="font-semibold text-forest">{selected.size}</span> of {docs.length} documents selected
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="lender-pack-cancel"
              className="btn-ghost-dark"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={build}
              disabled={working || loading}
              data-testid="lender-pack-build"
              className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
            >
              {working ? (
                <>
                  <Loader2 size={14} strokeWidth={2} className="animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Send size={14} strokeWidth={2} />
                  Build and download
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
